import React from 'react';
import { fontMap, ThemeFont } from '@swell/apps-sdk';
import pt from 'prop-types';

import { Field } from 'components/form';

import { WidgetContext } from './index';
import withWidget from './with-widget';

const fontOptions = fontMap.map((f) => ({
  value: f.family,
  label: f.label || f.family,
}));

function SwellFont({ value, field, onChange }) {
  const { swellTheme } = React.useContext(WidgetContext);

  const [state, setState] = React.useState(() => {
    const fontValue = swellTheme.resolveFontSetting(value);

    return {
      fontValue,
      variantValue: ThemeFont.variantToString(fontValue?.variant),
    };
  });

  const { fontValue, variantValue } = state;

  const onChangeFamily = React.useCallback(
    (event, value) => {
      const newFont = ThemeFont.findByFamily(value);

      if (newFont) {
        // Try to match variant with existing font weight/italic
        let variantMatch;

        if (fontValue) {
          const { wght, ital } = fontValue.variant;

          variantMatch =
            newFont.variants.find((v) => v.wght === wght && v.ital === ital) ||
            newFont.variants.find((v) => v.wght === wght);
        }

        if (!variantMatch) {
          variantMatch = newFont.variants[0];
        }

        const newFontValue = new ThemeFont({
          family: value,
          variant: variantMatch,
        });

        setState((state) => ({
          ...state,
          fontValue: newFontValue,
          variantValue: variantMatch
            ? ThemeFont.variantToString(variantMatch)
            : null,
        }));

        onChange(event, newFontValue.id);
      } else {
        setState((state) => ({
          ...state,
          fontValue: null,
          variantValue: null,
        }));

        onChange(event, null);
      }
    },
    [fontValue, onChange],
  );

  const onChangeVariant = React.useCallback(
    (event, value) => {
      if (value) {
        const newFontValue = new ThemeFont(`${fontValue.family}:${value}`);

        setState((state) => ({
          ...state,
          fontValue: newFontValue,
          variantValue: value,
        }));

        onChange(event, newFontValue.id);
      } else {
        setState((state) => ({
          ...state,
          fontValue: null,
          variantValue: null,
        }));

        onChange(event, null);
      }
    },
    [fontValue, onChange],
  );

  const fontDef = React.useMemo(
    () => ThemeFont.findByFamily(fontValue?.family),
    [fontValue],
  );

  const hasVariants = fontDef?.variants.length > 0;

  const variantOptions = React.useMemo(
    () => (fontDef ? getFontVariantOptions(fontDef) : []),
    [fontDef],
  );

  return (
    <>
      <Field
        type="select"
        name={field.prop}
        label={field.label}
        value={fontValue?.family}
        options={fontOptions}
        onChange={onChangeFamily}
        hint={hasVariants ? undefined : field.description}
      />

      {hasVariants && (
        <Field
          type="select"
          label={`${field.label} style`}
          value={variantValue}
          options={variantOptions}
          onChange={onChangeVariant}
          hint={hasVariants ? field.description : undefined}
        />
      )}
    </>
  );
}

SwellFont.propTypes = {
  field: pt.object,
  value: pt.string,

  onChange: pt.func,
};

function getFontVariantOptions(fontDef) {
  return fontDef.variants.map((variant) => ({
    value: ThemeFont.variantToString(variant),
    label: getFontVariantLabel(variant),
  }));
}

function getFontWeightLabel(weight) {
  switch (weight) {
    case 100:
      return 'Thin';
    case 200:
      return 'Extra Light';
    case 300:
      return 'Light';
    case 400:
      return 'Regular';
    case 500:
      return 'Medium';
    case 600:
      return 'Semi Bold';
    case 700:
      return 'Bold';
    case 800:
      return 'Extra Bold';
    case 900:
      return 'Black';
    default:
      return 'Regular';
  }
}

function getFontVariantLabel(variant) {
  let label = getFontWeightLabel(variant.wght);

  if (variant.ital) {
    if (variant.wght === 400) {
      label = 'Italic';
    } else {
      label = `${label} Italic`;
    }
  }

  return label;
}

export default React.memo(withWidget(SwellFont));
